import { GridSortModel, GridSortDirection, GridFilterModel, GridLinkOperator } from "@mui/x-data-grid-pro";

export const parseSortModel = (sortStr: string): GridSortModel => {
  return sortStr
    .split(",")
    .map((columnSpecStr) => columnSpecStr.split("+"))
    .map(([column, direction]) => ({
      field: column,
      sort: direction as GridSortDirection,
    }));
};

export const serializeSortModel = (sortModel: GridSortModel): string => {
  return sortModel.map(({ field, sort }) => `${field}+${sort}`).join(",");
};

export type OrderBy = "asc" | "desc" | "asc_nulls_first" | "asc_nulls_last" | "desc_nulls_first" | "desc_nulls_last";

// Filters saved before percent-encoding shipped can contain a raw "%" (e.g. a value like "50%"),
// which isn't a valid escape sequence -- decodeURIComponent throws on that input rather than
// passing it through, and this runs during render (via a useMemo on searchParams), so an
// uncaught throw here would crash the page instead of just leaving that one field undecoded.
const decodeFilterField = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const parseFilterModelFromSearchParams = (searchParams: URLSearchParams): GridFilterModel | undefined => {
  const filterStr = searchParams.get("filter") || "";
  if (!filterStr) {
    return undefined;
  }

  const filterOperator: GridLinkOperator =
    (searchParams.get("filterOperator") as GridLinkOperator) || GridLinkOperator.And;

  return {
    items: filterStr
      .split(",")
      .map((columnSpecStr) => columnSpecStr.split(":").map(decodeFilterField))
      .map(([id, columnField, operatorValue, value]) => ({
        id,
        columnField,
        operatorValue,
        value,
      })),
    linkOperator: filterOperator,
  };
};

/**
 * Serializes a filter model to a URLSearchParams object, extending existing search params if provided.
 * Does not modify the existing search params object, but returns a new one.
 * @param filterModel The filter model to serialize
 * @param existingParams Existing search parameters, if any
 * @returns A URLSearchParams object
 */
export const filterModelToSearchParams = (
  filterModel: GridFilterModel,
  existingParams?: URLSearchParams,
): URLSearchParams => {
  const params = existingParams ? new URLSearchParams(existingParams) : new URLSearchParams();

  // Drop items with no value entered -- otherwise `${value}` stringifies `undefined` into the
  // literal 4-character string "undefined", which survives the URL round-trip and gets sent to
  // Hasura as a real filter value (e.g. rejected as an invalid enum).
  const items = filterModel.items.filter(({ value }) => value !== undefined && value !== null && value !== "");

  if (items.length === 0) {
    params.delete("filter");
    params.delete("filterOperator");
    return params;
  }

  params.set(
    "filter",
    items
      .map(({ id, columnField, operatorValue, value }) =>
        // Each field is percent-encoded before joining with `:` (and items with `,`) so a filter
        // value that itself contains a `:` or `,` — e.g. a business name like "Acme, LLC" — can't
        // be misread as a field/item separator and silently truncated or split apart on parse.
        [id, columnField, operatorValue, value].map(encodeURIComponent).join(":"),
      )
      .join(","),
  );
  params.set("filterOperator", filterModel.linkOperator || "and");

  return params;
};
